import { useEffect, useRef, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router';

import {
  Box,
  Button,
  FormControlLabel,
  Grid,
  Typography,
  useTheme,
} from '@material-ui/core';

import { useStyles } from './login-page-styles';

import { makeUtilClasses } from '../../../theme/util-make-styles';
import { ApiError } from '../../../../model/app/errors/api-erros';
import { LoginFormModel } from '../../../../model/app/forms/master/login-form';
import { KdsStorageKeys, useKdsStorage, useKdsToken, useSessaoAtual, useToastSaurus } from '../../../../services/app';
import { CheckboxDefault } from '../../../../views/components/checkboxes';
import { FormLogin } from '../../../components/form/master/login/form-login';
import { CircularLoading } from '../../../components/utils';
import { DefaultFormRefs } from '../../../components/form/utils/form-default-props';
import { LoginIcon, UserIcon } from '../../../components/icons';
import { PublicPageHeader } from '../../../components/headers/header-public-page/header-public-page';
import { useThemeQueries } from '../../../theme';
import { AmbienteFooter } from '../../../components/ambiente-footer/ambiente-footer';
import { useConfirm } from 'material-ui-confirm';
import { AutenticacaoPendenciaContratoModel } from '../../../../model/api/autenticacao-pendencia/autenticacao-pendencia';
import { useEventTools } from '../../../../services/app/use-cases/events/event-tools';
import { AppEventEnum } from '../../../../model/enums/enum-app-event';
import { EnumModulo } from '../../../../model/enums/enum-modulo';
import { VariaveisAmbiente } from '../../../../config';
import { EnumDeviceType } from '../../../../model/enums/enum-device-type';
import { consumirDadosPendentesBufferAbrirAppNativo, useAbrirApp } from '../../../../hooks/abrir-app';
import { useActionQuery } from '../../../../services/app/stores/action-query';
import { useEmpresaAtual } from '../../../../services/app/use-cases/empresa-atual';
import isEmpty from 'lodash/isEmpty';
import { extrairParametros } from '../../../../utils/extrair-parametros';

/** Gravado pelo MainActivity Android ao abrir o app via intent (ver cordova_dadosAoReceber). */
export const CORDOVA_DADOS_SESSION_KEY = "cordova_dadosAoReceber";

const LoginPage = () => {
  const utilClasses = makeUtilClasses();
  const { isMobile } = useThemeQueries();
  const classes = useStyles();
  const { showToast } = useToastSaurus();
  const history = useHistory();
  const theme = useTheme();
  const { logar, carregando, usuario, deslogar, setarUser } = useSessaoAtual();
  const [bloqueado, setBloqueado] = useState(false);
  const chkManterConectado = useRef<HTMLInputElement>();
  const loginRefs = useRef<DefaultFormRefs<LoginFormModel>>(null);
  /** Impede reentrada com o mesmo payload (evento + sessionStorage + URL quase ao mesmo tempo). */
  const ultimoDadosIntegracaoRef = useRef<string>("");
  const validarAcessoTokenRef = useRef(true);
  const confirm = useConfirm()
  const { getRegistro, setRegistro } = useKdsStorage();
  const { addHandler, removeHandler } = useEventTools();
  const { convertToken } = useKdsToken();
  const { search } = useLocation()
  const { getDadosFromUrl } = useAbrirApp()
  const { setActionQuery } = useActionQuery()
  const { getEmpresaAtual } = useEmpresaAtual()

  const redirect = useCallback(() => {
    const redirectUrl = new URLSearchParams(search).get('redirect');
    if (redirectUrl) {
      return atob(redirectUrl);
    }
    return undefined;
  }, [search]);

  useEffect(() => {
    if (loginRefs) loginRefs.current?.fillForm(new LoginFormModel('', ''));
  }, [usuario]);

  //logar funçao
  const efetuarLogin = async (model: LoginFormModel) => {
    try {
      const manterConectado =
        chkManterConectado.current?.querySelector('input')?.checked === true;
      const res: any = await logar(model.usuario, model.senha, '', manterConectado);
      if (res?.status) {
        if (res.status === 402) {
          setRegistro(KdsStorageKeys.PendenciaContrato, res?.data as AutenticacaoPendenciaContratoModel)
          return history.push('/pendencias')
        }
      }

      if (typeof res !== 'string') {
        return history.push({
          pathname: '/selecionar-contrato',
          state: {
            email: model.usuario,
            senha: btoa(model.senha),
            contratos: res
          }
        })
      }

      setBloqueado(true);
      const redirecionar = redirect();
      if (redirecionar) {
        if (redirecionar.includes('pedidos-para-entrega')) {
          setRegistro(KdsStorageKeys.SelecaoModulo, { modulo: EnumModulo.Motoboy }, false)
          setTimeout(() => {
            history.push(redirecionar);
          }, 200)
          return
        }
      }
      history.push('/');
    } catch (e: any) {
      if (e.constructor.name === ApiError.name) {
        history.push(`/ativar-email?email=${model.usuario}`);
        return;
      } else {
        showToast('error', e.message);
        loginRefs.current?.resetForm();
      }
    }
  };

  const redirectToLanding = useCallback(() => {
    history.push('/');
  }, [history]);

  useEffect(() => {
    if (usuario) {
      if (usuario.confirmado !== 'True') {
        confirm({
          title: 'E-mail não confirmado.',
          description: `Atenção, o e-mail "${usuario.email}" ainda não foi confirmado. Pedimos para que seja confirmado antes que o acesso seja bloqueado.`,
          confirmationButtonProps: { variant: 'contained' },
          confirmationText: 'OK',
        }).then(() => {
          redirectToLanding();
        });
        return;
      }
    }
  }, [usuario, confirm, redirectToLanding]);

  useEffect(() => {
    addHandler(AppEventEnum.LoginPush, () => {
      history.push('/')
    })
    return () => {
      removeHandler(AppEventEnum.LoginPush, () => {
        history.push('/')
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addHandler, removeHandler])

  const setActionSeExistir = useCallback((action: string | undefined) => {
    if (action) {
      setActionQuery(action)
    }
  }, [setActionQuery])

  const processarLoginComDadosComprimidos = useCallback(async (dadosCompressed: string) => {
    if (!validarAcessoTokenRef.current || !dadosCompressed) {
      return;
    }

    const tokenLocal = getRegistro(KdsStorageKeys.Token, false);
    const dadosFromUrl = getDadosFromUrl(dadosCompressed);

    if (!dadosFromUrl) {
      return;
    }
    if (ultimoDadosIntegracaoRef.current === dadosCompressed) {
      return;
    }
    ultimoDadosIntegracaoRef.current = dadosCompressed;

    const tokenConvertido = convertToken(dadosFromUrl?.token ?? "");
    const tokenLocalConvertido = convertToken(tokenLocal ?? "");

    if (tokenConvertido?.apelido === tokenLocalConvertido?.apelido) {
      setActionSeExistir(dadosFromUrl?.action);
      if (dadosFromUrl?.empresaId && getEmpresaAtual()?.id !== dadosFromUrl?.empresaId) {
        try {
          await setarUser(dadosFromUrl?.token, true, dadosFromUrl?.empresaId);
        } catch (err: any) {
          console.log(err);
        }
      }
      validarAcessoTokenRef.current = false;
      history.push("/");
    } else if (isEmpty(tokenLocal)) {
      confirm({
        title: "Autorizar Login Automático",
        description: (
          <Box>
            <Typography variant="body1">
              Uma solicitação de login automático foi recebida. Ao confirmar, você permitirá acesso imediato à sua conta sem necessidade de inserir credenciais manualmente
            </Typography>
            <Box pt={2}>
              <Typography variant="body2">
                Entrar com a Conta: <Typography variant="body2" color="primary">{tokenConvertido?.apelido}</Typography>
              </Typography>
            </Box>
          </Box>
        ),
        confirmationButtonProps: { variant: "contained" },
        confirmationText: "Confirmar",
        cancellationText: "Cancelar",
        dialogProps: {
          disableEscapeKeyDown: true,
          disableBackdropClick: true
        },
      })
        .then(async () => {
          try {
            if (dadosFromUrl?.token || dadosFromUrl?.empresaId) {
              await setarUser(dadosFromUrl.token, true, dadosFromUrl?.empresaId);
              validarAcessoTokenRef.current = false;
            }
            setActionSeExistir(dadosFromUrl?.action);
            history.push("/");
          } catch (err: any) {
            showToast("error", err.message);
          }
        })
        .catch(() => {
          history.push("/");
        });
    } else {
      confirm({
        title: "Trocar de Conta?",
        description: (
          <Box>
            <Typography variant="body1">
              Olá! Identificamos uma solicitação para entrar automaticamente em uma nova conta. Para continuar, precisaremos desconectar a conta atual deste aplicativo.
            </Typography>
            <Box pt={2} flex flexDirection="row" justifyContent="space-around">
              <Typography variant="body2">
                Logado Atualmente: <Typography variant="body2" color="primary">{usuario?.apelido}</Typography>
              </Typography>
              <Typography variant="body2">
                Entrar com a Conta: <Typography variant="body2" color="primary">{tokenConvertido?.apelido}</Typography>
              </Typography>
            </Box>
          </Box>
        ),
        confirmationButtonProps: { variant: "contained" },
        confirmationText: "Sim, Trocar",
        cancellationText: "Manter Conta Atual",
        dialogProps: {
          disableEscapeKeyDown: true,
          disableBackdropClick: true
        },

      })
        .then(async () => {
          try {
            validarAcessoTokenRef.current = false;
            if (usuario) {
              deslogar(usuario?.usuarioId || "");
            }

            if (dadosFromUrl?.token || dadosFromUrl?.empresaId) {
              await setarUser(dadosFromUrl?.token, true, dadosFromUrl?.empresaId);
            }
            setActionSeExistir(dadosFromUrl?.action);
            history.push("/");
          } catch (err: any) {
            showToast("error", err.message);
            validarAcessoTokenRef.current = true;
          }
        })
        .catch(() => {
          history.push("/");
        });
    }
  }, [confirm, convertToken, deslogar, getDadosFromUrl, getEmpresaAtual, getRegistro, history, setActionSeExistir, setarUser, showToast, usuario]);

  const validarAcessoToken = useCallback(async () => {
    if (!validarAcessoTokenRef.current) {
      return;
    }

    const { dados: dadosNaUrl } = extrairParametros(window.location.href);
    let dados = dadosNaUrl;

    if (!dados) {
      try {
        dados = sessionStorage.getItem(CORDOVA_DADOS_SESSION_KEY);
        if (dados) {
          sessionStorage.removeItem(CORDOVA_DADOS_SESSION_KEY);
        }
      } catch {
        // ignore
      }
    }

    if (!dados) {
      return;
    }

    await processarLoginComDadosComprimidos(dados);
  }, [processarLoginComDadosComprimidos]);

  /**
   * Cordova: dados ficam no buffer Java até o JS estar pronto (bundle pesado).
   * Polling leve até consumir ou desistir; não depende do primeiro tick do React.
   */
  useEffect(() => {
    if (
      VariaveisAmbiente.paymentDevice !== EnumDeviceType.CORDOVA &&
      VariaveisAmbiente.paymentDevice !== EnumDeviceType.CORDOVA_POS
    ) {
      return;
    }
    let cancelled = false;
    let tentativas = 0;
    const maxTentativas = 60;
    let id: number | undefined;

    const tryPull = async (): Promise<boolean> => {
      if (cancelled || !validarAcessoTokenRef.current) {
        return true;
      }
      const raw = await consumirDadosPendentesBufferAbrirAppNativo();
      if (!raw) {
        return false;
      }
      await processarLoginComDadosComprimidos(raw);
      return true;
    };

    void tryPull().then((feito) => {
      if (feito && id !== undefined) {
        window.clearInterval(id);
      }
    });

    id = window.setInterval(async () => {
      tentativas += 1;
      if (cancelled || tentativas > maxTentativas || !validarAcessoTokenRef.current) {
        if (id !== undefined) {
          window.clearInterval(id);
        }
        return;
      }
      const feito = await tryPull();
      if (feito && id !== undefined) {
        window.clearInterval(id);
      }
    }, 500);

    return () => {
      cancelled = true;
      if (id !== undefined) {
        window.clearInterval(id);
      }
    };
  }, [processarLoginComDadosComprimidos]);

  useEffect(() => {
    const onDadosCordova = (ev: Event) => {
      const custom = ev as CustomEvent<string>;
      const detail = custom.detail;
      if (typeof detail !== "string" || !detail) {
        return;
      }
      void processarLoginComDadosComprimidos(detail);
    };
    window.addEventListener("dadosAoReceber", onDadosCordova);
    return () => window.removeEventListener("dadosAoReceber", onDadosCordova);
  }, [processarLoginComDadosComprimidos]);

  /** O nativo pode gravar sessionStorage depois do primeiro paint; a mesma aba não recebe "storage" event. */
  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 24;
    const id = window.setInterval(() => {
      attempts += 1;
      if (!validarAcessoTokenRef.current || attempts > maxAttempts) {
        window.clearInterval(id);
        return;
      }
      try {
        const pending = sessionStorage.getItem(CORDOVA_DADOS_SESSION_KEY);
        if (pending) {
          sessionStorage.removeItem(CORDOVA_DADOS_SESSION_KEY);
          void processarLoginComDadosComprimidos(pending);
          window.clearInterval(id);
        }
      } catch {
        // ignore
      }
    }, 500);
    return () => window.clearInterval(id);
  }, [processarLoginComDadosComprimidos]);

  useEffect(() => {
    void validarAcessoToken();
  }, [validarAcessoToken]);

  return (
    <>
      <PublicPageHeader topOpacity="0.8" />

      <div className={utilClasses.contentCenter}>
        <Grid container className={utilClasses.rootContainer}>
          {carregando || bloqueado ? (
            <CircularLoading tipo="FULLSIZED" />
          ) : null}
          <Grid item xs={12}>
            <Grid container alignItems="center" direction="column">
              <Grid item xs={10} md={4}>
                <Typography align="center" variant="h3" color="primary">
                  <UserIcon tipo="GERAL" class={classes.imgTitulo} />
                  Login
                </Typography>
                <Box mt={3}>
                  <Typography align="center" variant="body1">
                    Olá, seja muito bem vindo(a) ao Acompanhamento de Processos (KDS)! Informe seu e-mail ou apelido para ingressar.
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={8} lg={6} style={{ width: '100%' }}>
                <FormLogin
                  showLoading={false}
                  ref={loginRefs}
                  loading={carregando}
                  onSubmit={(model) => {
                    efetuarLogin(model);
                  }}

                />
                <Grid item xs={12}>
                  <Grid
                    container
                    direction={isMobile ? 'column' : 'row-reverse'}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <FormControlLabel
                        disabled={carregando || bloqueado}
                        ref={chkManterConectado}
                        control={<CheckboxDefault />}
                        label="Mantenha-me conectado"
                      />
                    </Box>
                  </Grid>
                </Grid>
                <Box mt={4}>
                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      color="primary"
                      variant="contained"
                      fullWidth={true}
                      disabled={carregando || bloqueado}
                      size="large"
                      onClick={() => {
                        loginRefs.current?.submitForm();
                      }}
                    >
                      <LoginIcon tipo="BUTTON_PRIMARY" />
                      Acessar
                    </Button>
                  </Grid>
                </Box>
                <Grid item xs={12}>
                  <Box my={1}>
                    <Grid
                      container
                      justifyContent="center"
                      alignItems="center"
                      style={{ color: theme.palette.divider }}
                    >
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <AmbienteFooter />
    </>
  );
};

export default LoginPage;
